/**
 * 卖家商品域 API(W-7 发布 / W-8 管理)。控制器全 @IdentityStateRequired(KYC_VERIFIED)。
 *
 * 注意:生成的 `SellerProductListResponse`/`SellerProductDetailResponse` 是 envelope 包装(递归自引用),
 * 拿不到内层字段;`call<T>()` 已解包 `envelope.data`,故此处按后端 DTO 契约**本地声明内层 payload 形状**。
 * 请求体与扁平响应(Publish/Edit/ChangeStatus/CoverCredential)直接用生成类型。
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];

export type ProductStatus = 'DRAFT' | 'PENDING_AUDIT' | 'ONLINE' | 'OFFLINE' | 'REJECTED';
// iter-010 实物扩类(30-2):PIECE(件) / EXPRESS(快递) / PICKUP(自提)。契约里 unit/medium 为 string,此 union 仅前端约束。
export type ProductUnit = 'TIMES' | 'SESSIONS' | 'HOURS' | 'PIECE';
export type ProductMedium = 'IM' | 'VOICE' | 'VIDEO' | 'OFFLINE' | 'EXPRESS' | 'PICKUP';
export type ChangeStatusTarget = 'OFFLINE' | 'ONLINE';

export type PublishProductRequest = Schemas['PublishProductRequest'];
export type PublishProductResponse = Schemas['PublishProductResponse'];
export type EditProductRequest = Schemas['EditProductRequest'];
export type EditProductResponse = Schemas['EditProductResponse'];
export type ChangeProductStatusResponse = Schemas['ChangeProductStatusResponse'];
export type ProductCoverUploadCredentialResponse = Schemas['ProductCoverUploadCredentialResponse'];

/** 列表行(轻量:单封面 coverImage,无 medium/category/description)。 */
export interface SellerProductListItem {
  id: string;
  title: string;
  coverImage?: string;
  price: number; // 分
  unit: ProductUnit;
  status: ProductStatus;
  rejectionReason?: string | null;
  salesCount: number;
  rating?: number;
  updatedAt?: string;
}
export interface SellerProductList {
  items: SellerProductListItem[];
}

/** 详情(编辑预填,全字段)。 */
export interface SellerProductDetail {
  id: string;
  title: string;
  description: string;
  price: number; // 分
  unit: ProductUnit;
  categoryL1Id: string;
  categoryL2Id: string;
  medium: ProductMedium;
  coverImages: string[];
  status: ProductStatus;
  rejectionReason?: string | null;
  salesCount: number;
  rating?: number;
}

const BASE = '/api/v1/guide/seller/products';

export function listSellerProducts(): Promise<SellerProductList> {
  return call<SellerProductList>({ method: 'GET', url: BASE });
}

export function getSellerProduct(id: string): Promise<SellerProductDetail> {
  return call<SellerProductDetail>({ method: 'GET', url: `${BASE}/${id}` });
}

export function publishProduct(req: PublishProductRequest): Promise<PublishProductResponse> {
  return call<PublishProductResponse>({ method: 'POST', url: BASE, data: req });
}

export function editProduct(id: string, req: EditProductRequest): Promise<EditProductResponse> {
  return call<EditProductResponse>({ method: 'PUT', url: `${BASE}/${id}`, data: req });
}

export function changeProductStatus(
  id: string,
  targetStatus: ChangeStatusTarget,
): Promise<ChangeProductStatusResponse> {
  return call<ChangeProductStatusResponse>({
    method: 'PUT',
    url: `${BASE}/${id}/status`,
    data: { targetStatus } satisfies Schemas['ChangeProductStatusRequest'],
  });
}

/** 软删除商品(卖家仅可删自己的;后端 @TableLogic 逻辑删除 deleted=1)。 */
export function deleteProduct(id: string): Promise<void> {
  return call<void>({
    method: 'DELETE',
    url: `${BASE}/${id}`,
  });
}

export function getCoverUploadCredential(): Promise<ProductCoverUploadCredentialResponse> {
  return call<ProductCoverUploadCredentialResponse>({
    method: 'GET',
    url: `${BASE}/cover-upload-credential`,
  });
}
